/* Mariposa Mail Order ship/arrival estimate — one implementation, two callers.

   A8(a) destination transit estimates and A8(b) the holiday-cutoff sentence.
   Everything here is computed from the bundled `mariposa.schedule` metafield —
   the SAME record the checkout Function and the cron read — so the cart, the
   product page and the till never disagree. Nothing about her policy is
   hardcoded: the cutoff hour, the ship weekdays, the delivery weekdays, the
   blackout dates and their names, and the per-destination transit ranges all
   come out of `channels.mail`.

   The transit table itself is hers, read off her live Mail Order store on
   2026-09-05 and corroborated three ways (her Omega EDD app rules, 1,536 real
   delivered shipments, and live FedEx quotes). See
   tools/mail-transit-config-2026-09-05.cjs for the derivation.

   Exposes window.MariposaShip. Loaded on every page from layout/theme.liquid,
   BEFORE mariposa-scheduler.js, which calls into it for the cart. Also
   self-renders any [data-mship] block on the page (that is how the PDP uses it)
   so a caller only has to emit markup plus a config <script>. */
(function () {
  'use strict';

  var DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var DAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
    'August', 'September', 'October', 'November', 'December'];

  var DEST_KEY = 'mariposa_dest';

  function pad(n) { return n < 10 ? '0' + n : '' + n; }
  function iso(d) { return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); }
  function parseIso(s) {
    if (!s) return null;
    var p = String(s).split('-');
    if (p.length !== 3) return null;
    var d = new Date(parseInt(p[0], 10), parseInt(p[1], 10) - 1, parseInt(p[2], 10));
    return isNaN(d.getTime()) ? null : d;
  }

  /* The bakery clock. Every cutoff in this file is America/Los_Angeles, never
     the buyer's zone — a Chicago shopper at 1:30pm local is 11:30am here and
     still inside the noon cutoff. Same helper shape as mariposa-scheduler.js's
     nowLA(); duplicated deliberately so this file has no load-order dependency
     on that one. */
  function nowLA() {
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Los_Angeles',
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: 'numeric', hourCycle: 'h23',
      }).formatToParts(new Date());
      var o = {};
      parts.forEach(function (x) { o[x.type] = x.value; });
      var d = new Date(+o.year, +o.month - 1, +o.day);
      d.laHour = +o.hour;
      d.laMinute = +o.minute;
      return d;
    } catch (e) {
      var n = new Date();
      var d2 = new Date(n.getFullYear(), n.getMonth(), n.getDate());
      d2.laHour = n.getHours();
      d2.laMinute = n.getMinutes();
      return d2;
    }
  }

  function fmtLong(d) {
    return DAY_LONG[d.getDay()] + ', ' + MONTH_LONG[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear();
  }
  function fmtShort(d) {
    return DAY_SHORT[d.getDay()] + ', ' + MONTH_LONG[d.getMonth()].slice(0, 3) + ' ' + d.getDate();
  }

  /* ---------------------------------------------------------------- destination

     The cart page has no shipping address — Shopify does not collect one until
     checkout — so the estimate uses the best destination the page actually has,
     in this order, and says which one it used:

       1. a ZIP the shopper typed into the estimate box (kept in localStorage so
          it survives the walk from a product page to the cart),
       2. the signed-in customer's default address, passed in from Liquid,
       3. nothing, in which case the range is shown as "N-M days after it ships"
          rather than as dates. That is the pre-existing behaviour, unchanged.

     Never guessed from an IP or a locale: a wrong ZIP produces a wrong promise. */
  function readDest() {
    try {
      var raw = window.localStorage.getItem(DEST_KEY);
      if (raw) {
        var v = JSON.parse(raw);
        if (v && (v.zip || v.state)) return { zip: v.zip || '', state: v.state || '', source: 'shopper' };
      }
    } catch (e) { /* private mode, blocked storage — fall through */ }
    return null;
  }

  function saveDest(zip, state) {
    try {
      window.localStorage.setItem(DEST_KEY, JSON.stringify({ zip: zip || '', state: state || '' }));
    } catch (e) { /* nothing to do; the estimate still renders for this page */ }
    if (typeof applyCartNote === 'function') applyCartNote();
  }

  function clearDest() {
    try { window.localStorage.removeItem(DEST_KEY); } catch (e) { /* ignore */ }
    if (typeof applyCartNote === 'function') applyCartNote();
  }

  function dest(cfg) {
    var chosen = readDest();
    if (chosen) return chosen;
    if (cfg && (cfg.destZip || cfg.destState)) {
      return { zip: cfg.destZip || '', state: cfg.destState || '', source: 'account' };
    }
    return { zip: '', state: '', source: 'none' };
  }

  /* What the dates on screen are actually an estimate FOR. The block used to
     read " to Northern California" — a zone name, not the thing the shopper can
     change — so a destination inherited from a saved address, or left over in
     localStorage from an earlier visit, looked like a fact about their order.
     Name the ZIP, name where it came from, and the "Change ZIP" control beside
     it is then obviously the way to correct it. */
  function destLabel(where, t) {
    if (!where || where.source === 'none') return '';
    var who = where.zip ? 'ZIP ' + where.zip : where.state;
    var zone = t && t.label ? ' (' + t.label + ')' : '';
    return ' to ' + who + zone;
  }

  /* The standing caveat under the dates. With a destination it says, in the one
     place a shopper reads before checking out, that the address they type at
     checkout is what settles the arrival — because that address is the one thing
     this page cannot see. */
  /* A saved group order ships to the recipients' addresses, not to the address
     the buyer types at checkout — sections/mariposa-gift-recipients.liquid says
     so, and until 2026-09-10 this note said the opposite on the same visit
     ("The delivery address you enter at checkout is the one we ship to"). A
     tester read both and concluded everything was coming to them. */
  function groupNote(where, t) {
    var parts = ['Multiple recipient order: each recipient\'s address is shipped to separately.'];
    if (where && where.source !== 'none') {
      parts.push('Dates above are estimated for ' + (where.zip ? 'ZIP ' + where.zip : where.state) +
        '; each box\'s arrival depends on its own address.');
    }
    if (t && t.note) parts.push(t.note);
    return parts.join(' ');
  }

  function estimateNote(where, t, channel) {
    var parts = [channel === 'ws_ship' ? 'Final delivery estimates depend on the shipping service selected at checkout.' : 'Mariposa Flat Rate estimate. Other shipping options and their estimates appear at checkout.'];
    if (where && where.source !== 'none') {
      parts.push('Estimated for ' + (where.zip ? 'ZIP ' + where.zip : where.state) +
        (where.source === 'account' ? ', your saved address' : '') +
        '. The delivery address you enter at checkout is the one we ship to.');
    }
    if (t && t.note) parts.push(t.note);
    return parts.join(' ');
  }

  /* ------------------------------------------------------------------- transit

     channels.mail.transit_days is either the legacy scalar (one number of days,
     what ws_ship still carries) or the destination table:

       { "default": [2,3],
         "zones": [ {id, label, zip3:[...], states:[...], days:[min,max]} ],
         "note": "..." }

     Zones are tested in order and the first match wins, so the narrowest zone
     must be listed first — which is how the writer emits them (Northern
     California ZIP3s before the whole of California). */
  function normalizeTransit(t) {
    if (t == null) return null;
    if (typeof t === 'number') return { default: [t, t], zones: [], note: '' };
    var def = t.default;
    if (typeof def === 'number') def = [def, def];
    if (!Array.isArray(def) || def.length !== 2) return null;
    return { default: def, zones: Array.isArray(t.zones) ? t.zones : [], note: t.note || '' };
  }

  function transitFor(transit, where) {
    var t = normalizeTransit(transit);
    if (!t) return null;
    var zip3 = where && where.zip ? String(where.zip).replace(/\D/g, '').slice(0, 3) : '';
    var state = where && where.state ? String(where.state).toUpperCase() : '';
    for (var i = 0; i < t.zones.length; i++) {
      var z = t.zones[i];
      if (!z || !Array.isArray(z.days)) continue;
      if (zip3 && Array.isArray(z.zip3) && z.zip3.indexOf(zip3) !== -1) {
        return { days: z.days, label: z.label || '', zone: z.id || '', note: t.note, matched: 'zip' };
      }
      if (state && Array.isArray(z.states) && z.states.indexOf(state) !== -1) {
        return { days: z.days, label: z.label || '', zone: z.id || '', note: t.note, matched: 'state' };
      }
    }
    return { days: t.default, label: '', zone: '', note: t.note, matched: zip3 || state ? 'default' : 'none' };
  }

  /* ------------------------------------------------- destination-independent

     EVERYTHING STAMPED ONTO THE CART OR ONTO A LINE IS COMPUTED FROM THESE.

     Shopify's checkout renders line-item properties and cart attributes exactly
     as the cart wrote them; no theme script runs there (verified 2026-09-06 on a
     real checkout — `window.MariposaShip` is undefined and the only scripts on
     the page are Shopify's own). So a value stamped in the cart CANNOT be
     re-computed once the shopper types their real address, and any stamped value
     that depends on the destination becomes a false statement the moment the two
     differ. Measured on that same checkout: cart estimator at ZIP 94609, address
     Brooklyn 11201, line reading "Estimated arrival: Wed, Sep 9 - Thu, Sep 10"
     (the Northern California answer) beside correct Brooklyn FedEx rates.

     The fix is not to guess better. It is to stamp only statements that hold for
     every US destination:

       nationalDays()  the slowest lane — `transit_days.default`, which every
                       zone is faster than or equal to. An arrival quoted from it
                       can be beaten but never missed.
       fastestDays()   the quickest zone, used only to ask whether the ship day
                       is the same for everyone. When it is, the exact date is
                       stamped; when it is not (her Thursday, offered to the
                       1-2 day zones and withheld from the rest), the stamp says
                       "by <the latest date>", which is true for both.

     The on-page estimate is unchanged: with a ZIP the shopper typed it still
     shows real dates, labelled with that ZIP. */
  function nationalDays(ch) {
    var t = normalizeTransit(ch && ch.transit_days);
    return t ? t.default : null;
  }

  function fastestDays(ch) {
    var t = normalizeTransit(ch && ch.transit_days);
    if (!t) return null;
    var best = t.default;
    for (var i = 0; i < t.zones.length; i++) {
      var z = t.zones[i];
      if (!z || !Array.isArray(z.days) || z.days.length !== 2) continue;
      if (Math.max(z.days[0], z.days[1]) < Math.max(best[0], best[1])) best = z.days;
    }
    return best;
  }

  /* "2-3 days after it ships". The wording the block already used when it had no
     destination at all, reused so the cart, the line and the confirmation email
     say one thing. */
  function rangeWords(days) {
    if (!Array.isArray(days) || days.length !== 2) return '';
    return days[0] === days[1]
      ? days[0] + ' day' + (days[0] === 1 ? '' : 's') + ' after it ships'
      : days[0] + '–' + days[1] + ' days after it ships';
  }

  /* The ship day as it can honestly be written down before an address exists.
     `date` is the LATEST it can ship for any destination (the national roll);
     `text` is that date plainly when every destination shares it, and "by <date>"
     when a faster zone would go sooner. Returns date:null when nothing satisfies
     the rule, and the caller stamps nothing. */
  function neutralShip(start, ch) {
    var slow = rollToShipDay(start, ch, nationalDays(ch));
    if (!slow.date) return { date: null, exact: false, text: '' };
    var fast = rollToShipDay(start, ch, fastestDays(ch));
    var same = !!(fast.date && iso(fast.date) === iso(slow.date));
    return {
      date: slow.date,
      exact: same,
      text: same ? fmtLong(slow.date) : 'by ' + fmtLong(slow.date),
    };
  }

  /* --------------------------------------------------------------- ship / arrive

     closed_weekdays on a shipping channel is keyed "_" (no locations), holding
     the weekdays she does NOT ship — [0,5,6], Sunday/Friday/Saturday, i.e. she
     ships Monday to Thursday. delivery_weekdays is the opposite shape: the days
     a carrier will hand a parcel over, [2,3,4,5,6] Tue-Sat, from her own flat
     rate rule. Blackout dates block both. */
  function shipClosedDays(ch) {
    return (ch && ch.closed_weekdays && ch.closed_weekdays._) || [];
  }
  function blackouts(ch) { return (ch && ch.blackouts) || []; }
  function deliveryDays(ch) {
    var d = ch && ch.delivery_weekdays;
    return Array.isArray(d) && d.length ? d : null;
  }

  /* The effective receipt day: today when we are still inside this channel's
     cutoff on a day she ships, otherwise tomorrow. Mirrors computeShipBase() in
     web/cron/pickup-window.mjs, which writes channels.mail.today at 12:00 PT —
     recomputing it here means the sentence is right in the minutes between the
     noon cron runs instead of promising a ship day that has already closed. The
     result is never EARLIER than the server's, only later. */
  function shipBase(ch, cfg) {
    var serverBase = parseIso((ch && ch.today) || (cfg && cfg.today));
    var cutoffHour = ch && ch.cutoff && isFinite(ch.cutoff.hour) ? ch.cutoff.hour : 12;
    var weekdaysOnly = !(ch && ch.cutoff && ch.cutoff.weekdays_only === false);
    var now = nowLA();
    var applicable = weekdaysOnly ? now.getDay() >= 1 && now.getDay() <= 5 : true;
    var client = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    var before = now.laHour < cutoffHour || (ch.cutoff && ch.cutoff.inclusive_minute === true && now.laHour === cutoffHour && now.laMinute === 0);
    var pastCutoff = !(applicable && before);
    if (pastCutoff) client.setDate(client.getDate() + 1);
    if (!serverBase) return { date: client, pastCutoff: pastCutoff, cutoffHour: cutoffHour };
    return {
      date: client > serverBase ? client : serverBase,
      pastCutoff: pastCutoff,
      cutoffHour: cutoffHour,
    };
  }

  /* ------------------------------------------------------- arrive-by-Saturday

     Her published rule, www.mariposabaking.com/pages/shipping-information:

       "Your order will ship if it is received before noon on Thursday AND IS
        ESTIMATED TO ARRIVE BY SATURDAY. Sorry - FedEx does not offer delivery
        on Sunday."

     That starred caveat is the whole rule; "Monday-Wednesday for all US States,
     Monday-Thursday for CA, AZ, OR and WA" is its CONSEQUENCE, not a second
     rule. Nationally transit is 2-3 days, so a Thursday ship arrives Saturday at
     best and the following week at worst — over a weekend, on perishable food —
     which is why she does not offer Thursday nationally. Where transit is 1-2
     days a Thursday ship arrives Friday or Saturday and is fine.

     So the state list is DERIVED here, never typed. `channels.mail.transit_days`
     owns the zones; this file only asks "does the latest arrival land inside
     this ship day's own week?". Add AZ/OR/WA to the zone table and they gain
     Thursday with no change to this file; change a transit time and the offered
     days move with it. Grepping this file for a state code should find nothing.

     `closed_weekdays` stays the OUTER window (Mon-Thu). This is the narrower
     hold applied inside it. */

  /* The Saturday that closes this date's own week (0=Sun … 6=Sat). For a
     Saturday ship day that is the same day; for a Sunday it is six days on. */
  function weekEnd(d) {
    var e = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    e.setDate(e.getDate() + (6 - e.getDay()));
    return e;
  }

  /* transitDays is [min,max] for the destination — the caller passes
     transitFor(...).days, which already falls back to the NATIONAL DEFAULT when
     the page has no address. That fallback is deliberate and it is the strict
     direction: the default is the slowest lane, so an unknown destination is
     offered the narrowest set of ship days and can only ever be told LESS than
     it will be told once a ZIP is known. Showing a date and withdrawing it at
     checkout is the failure this exists to prevent.

     Returns true when there is no transit table at all: with nothing to compute
     an arrival from, the rule cannot be evaluated, and inventing an answer would
     be worse than the pre-existing behaviour. */
  function shipDayAllowed(shipDate, ch, transitDays) {
    if (ch && ch.arrive_by_saturday === false) return true;
    if (!Array.isArray(transitDays) || transitDays.length !== 2) return true;
    var maxDays = Math.max(transitDays[0], transitDays[1]);
    if (!isFinite(maxDays) || maxDays <= 0) return true;
    var latest = addDeliveryDays(shipDate, maxDays, ch);
    if (latest.getDay() === 0) return false;          // never a Sunday arrival
    var end=new Date(shipDate.getTime());
    if((ch.dispatch_dates || []).indexOf(iso(shipDate))!==-1)end.setDate(end.getDate()+7);
    return latest.getTime() <= weekEnd(end).getTime();
  }

  /* Forward to the first day she actually ships: not a closed weekday, not a
     blackout, AND one whose latest estimated arrival satisfies the rule above.
     Returns { date, skipped, held, exhausted }: `skipped` lists blackout dates
     stepped over so the caller can name the holiday, `held` lists open ship days
     rejected by the arrival rule so a guard can prove the rule did the work.

     BOUNDED. The search runs at most ROLL_LIMIT days and then gives up LOUDLY —
     `date: null`, `exhausted: true`, and a console error — rather than spinning
     or, worse, returning a date it has already decided is invalid. A config in
     which no ship day can ever arrive in time (say delivery_weekdays with no
     Saturday and a 3-day transit) is a configuration bug, and the shopper must
     see no estimate rather than a false one. Every caller checks `exhausted`. */
  var ROLL_LIMIT = 45;
  function rollToShipDay(start, ch, transitDays) {
    var closed = shipClosedDays(ch);
    var black = blackouts(ch);
    var d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    var skipped = [];
    var held = [];
    for (var i = 0; i < ROLL_LIMIT; i++) {
      var isBlack = black.indexOf(iso(d)) !== -1;
      if (((ch.dispatch_dates || []).indexOf(iso(d)) !== -1 || closed.indexOf(d.getDay()) === -1) && !isBlack) {
        if (shipDayAllowed(d, ch, transitDays)) {
          return { date: d, skipped: skipped, held: held, exhausted: false };
        }
        held.push(iso(d));
      } else if (isBlack) {
        skipped.push(iso(d));
      }
      d.setDate(d.getDate() + 1);
    }
    try {
      console.error('MariposaShip: no ship day within ' + ROLL_LIMIT + ' days of ' +
        iso(start) + ' can arrive by Saturday (transit ' + JSON.stringify(transitDays) +
        '). Check channels.<ch>.transit_days / delivery_weekdays / closed_weekdays.');
    } catch (e) { /* no console in some embeds; the exhausted flag still carries */ }
    return { date: null, skipped: skipped, held: held, exhausted: true };
  }

  /* Arrival = ship date plus N delivery days, counting only days a carrier
     delivers and never landing on a blackout. Her own flat-rate rule says
     "excludes Sun" and enables Tue-Sat, which is why a Thursday ship lands
     Saturday rather than Sunday. With no delivery_weekdays configured this
     degrades to plain calendar days, which is what the estimate did before. */
  function addDeliveryDays(shipDate, n, ch) {
    var days = deliveryDays(ch);
    var black = Array.isArray(ch.delivery_blackouts) ? ch.delivery_blackouts : blackouts(ch);
    var d = new Date(shipDate.getTime());
    var counted = 0;
    for (var i = 0; i < 60 && counted < n; i++) {
      d.setDate(d.getDate() + 1);
      if (days && days.indexOf(d.getDay()) === -1) continue;
      if (black.indexOf(iso(d)) !== -1) continue;
      counted++;
    }
    return d;
  }

  function arrival(shipDate, range, ch) {
    if (!Array.isArray(range)) return null;
    var lo = addDeliveryDays(shipDate, Math.max(0, range[0]), ch);
    var hi = addDeliveryDays(shipDate, Math.max(range[0], range[1]), ch);
    return { min: lo, max: hi, same: iso(lo) === iso(hi) };
  }

  function fmtArrival(a) {
    if (!a) return '';
    return a.same ? fmtShort(a.min) : fmtShort(a.min) + ' – ' + fmtShort(a.max);
  }

  /* ------------------------------------------------------------- cutoff notice

     A8(b). Her published line is "orders received after 12:00 pm PST will not
     ship until [next ship day]". Both halves are generated: the hour from
     channels.mail.cutoff.hour, the next ship day from the same roll-forward the
     estimate uses, and the holiday clause from blackout_dates plus the names in
     blackout_labels. Nothing to edit in the theme when a date moves. */
  function hourLabel(h) {
    if (h === 12) return '12:00 pm';
    if (h === 0) return '12:00 am';
    return (h > 12 ? h - 12 : h) + ':00 ' + (h >= 12 ? 'pm' : 'am');
  }

  /* "Monday through Wednesday" / "Monday through Thursday" — written out of the
     config and the arrival rule rather than typed, so changing the ship days or
     a transit time in the admin changes the sentence. Collapses a contiguous run
     into "X through Y" and falls back to a list otherwise. */
  function shipDayPhrase(ch, transitDays) {
    var closed = shipClosedDays(ch);
    var open = [];
    if (Array.isArray(transitDays)) {
      /* Say the days SHE CAN ACTUALLY SHIP TO THIS ADDRESS. closed_weekdays is
         the outer Mon-Thu window; the arrive-by-Saturday hold narrows it per
         destination, so a Brooklyn shopper told "Monday through Thursday" is
         being promised a Thursday the same page then refuses to offer — which
         is the sentence customer service gets quoted back. Verified at a real
         cart 2026-09-06: the line read "We ship Monday through Thursday" to ZIP
         11201 while the estimate had already rolled Thursday to Monday.

         Derived by asking the rule, over a real three-week span so blackouts and
         the delivery calendar are the ones actually in force. */
      var seen = {};
      var base = nowLA();
      var black = blackouts(ch);
      for (var k = 0; k < 21; k++) {
        var d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + k);
        if (closed.indexOf(d.getDay()) !== -1) continue;
        if (black.indexOf(iso(d)) !== -1) continue;
        if (!shipDayAllowed(d, ch, transitDays)) continue;
        seen[d.getDay()] = true;
      }
      for (var n = 0; n < 7; n++) if (seen[n]) open.push(n);
    } else {
      for (var i = 0; i < 7; i++) if (closed.indexOf(i) === -1) open.push(i);
    }
    if (!open.length) return 'on scheduled days';
    if (open.length === 1) return DAY_LONG[open[0]] + 's';
    var contiguous = true;
    for (var j = 1; j < open.length; j++) if (open[j] !== open[j - 1] + 1) contiguous = false;
    if (contiguous) return DAY_LONG[open[0]] + ' through ' + DAY_LONG[open[open.length - 1]];
    return open.map(function (n) { return DAY_LONG[n]; }).join(', ');
  }

  /* transitDays is optional and only matters when `shipIso` is absent: the
     callers that have already rolled pass the date they are showing, and this
     must quote THAT date rather than a second, differently-computed one. When it
     has to roll for itself it applies the same arrive-by-Saturday rule, so the
     sentence can never name a ship day the estimate above refused. */
  function cutoffNote(ch, bundle, shipIso, transitDays) {
    var base = shipBase(ch, bundle);
    var rolled = rollToShipDay(base.date, ch, transitDays);
    var shipDate = parseIso(shipIso) || rolled.date;
    var labels = (bundle && bundle.blackout_labels) || {};
    var hour = hourLabel(base.cutoffHour);

    /* No ship day satisfies the rule and no caller supplied one. Say which days
       she ships and stop — quoting a cutoff for a date we do not have would be
       inventing the one number the shopper acts on. */
    if (!shipDate) {
      return {
        text: 'No eligible ship date is currently available for this delivery estimate.',
        pastCutoff: base.pastCutoff,
        closed: [],
        shipDate: null,
        exhausted: true,
      };
    }

    /* Every blackout between now and the ship date, named where we have a name
       for it. This is what turns "we ship Tuesday" into "we ship Tuesday
       because we are closed Monday for Labor Day". */
    var black = blackouts(ch);
    var today = nowLA();
    var closedParts = [];
    for (var i = 0; i < black.length; i++) {
      var b = parseIso(black[i]);
      if (!b || b < today || b > shipDate) continue;
      var name = labels[black[i]];
      closedParts.push(DAY_LONG[b.getDay()] + ', ' + MONTH_LONG[b.getMonth()] + ' ' + b.getDate() +
        (name ? ' for ' + name : ''));
    }

    /* Three cases, and the cutoff only explains one of them. On a Saturday the
       reason the order ships Tuesday is that she does not ship at weekends, not
       that noon has passed — saying "orders after 12:00 pm will not ship until
       Tuesday" there would be true but misleading, and it is the sentence a
       shopper quotes back at customer service. */
    var closedToday = shipClosedDays(ch).indexOf(today.getDay()) !== -1 ||
      black.indexOf(iso(today)) !== -1;
    var sentence;
    if (closedToday) {
      sentence = 'Your estimated ship date is ' + fmtLong(shipDate) + '.';
    } else if (base.pastCutoff) {
      sentence = 'Orders received after ' + hour + ' PT will not ship until ' + fmtLong(shipDate) + '.';
    } else {
      sentence = 'Order by ' + hour + ' PT today and it ships ' + fmtLong(shipDate) + '.';
    }
    if (closedParts.length) {
      sentence += ' We are closed ' + closedParts.join(' and ') + ', so nothing leaves the bakery' +
        (closedParts.length > 1 ? ' on those days' : ' that day') + '.';
    }
    return { text: sentence, pastCutoff: base.pastCutoff, closed: closedParts, shipDate: shipDate };
  }

  /* ------------------------------------------------------------------ rendering

     Self-renders any [data-mship] element whose config lives in a child
     <script type="application/json" data-mship-config>. Used by the PDP; the
     cart calls the functions above directly from mariposa-scheduler.js because
     it also has cart lead days and the seasonal check to fold in. */
  function q(root, sel) { return root.querySelector(sel); }

  function renderBlock(root) {
    var holder = q(root, '[data-mship-config]');
    if (!holder) return;
    var cfg;
    try { cfg = JSON.parse(holder.textContent); } catch (e) { return; }
    var bundle = cfg.schedule || {};
    var ch = (bundle.channels && bundle.channels.mail) || {};

    var where = dest(cfg);
    var t = transitFor(ch.transit_days, where);

    var base = shipBase(ch, bundle);
    var lead = cfg.leadDays > 0 ? cfg.leadDays : 0;
    var start = new Date(base.date.getTime());
    if (lead) start.setDate(start.getDate() + lead);
    var padding=ch.prep_padding_days != null ? ch.prep_padding_days : (bundle.prep_padding_days || 0);
    for(var added=0,guard=0;added<padding && guard<120;guard++){
      start.setDate(start.getDate()+1);
      if(blackouts(ch).indexOf(iso(start))===-1 && ((ch.preparation_dates || []).indexOf(iso(start))!==-1 || (ch.preparation_weekdays ? ch.preparation_weekdays.indexOf(start.getDay())!==-1 : (shipClosedDays(ch).indexOf(start.getDay())===-1 || (ch.dispatch_dates || []).indexOf(iso(start))!==-1))))added++;
    }
    if(added<padding){root.hidden=true;return;}

    /* The destination's own transit range decides which ship days are offerable
       — `t.days` is the national default when the page has no address. */
    var rolled = rollToShipDay(start, ch, t && t.days);
    var ship = rolled.date;

    /* Nothing satisfies the rule. Show no block at all: a hidden estimate is
       recoverable, a wrong one is a promise she cannot keep. */
    if (!ship) { root.hidden = true; return; }

    var shipEl = q(root, '[data-mship-ship]');
    if (shipEl) { shipEl.textContent = fmtLong(ship); }

    var arriveEl = q(root, '[data-mship-arrive]');
    if (arriveEl && t) {
      if (where.source === 'none') {
        arriveEl.textContent = t.days[0] === t.days[1]
          ? t.days[0] + ' day' + (t.days[0] === 1 ? '' : 's') + ' after it ships'
          : t.days[0] + '–' + t.days[1] + ' days after it ships';
      } else {
        arriveEl.textContent = fmtArrival(arrival(ship, t.days, ch));
      }
    }

    var destEl = q(root, '[data-mship-dest]');
    if (destEl) destEl.textContent = destLabel(where, t);

    var editEl = q(root, '[data-mship-dest-edit]');
    if (editEl) editEl.textContent = where.source === 'none' ? 'Estimate for my ZIP code' : 'Change ZIP';

    var noteEl = q(root, '[data-mship-note]');
    var noteTxt = root.dataset.mshipGroup === '1' ? groupNote(where, t) : estimateNote(where, t);
    if (noteEl && noteTxt) { noteEl.textContent = noteTxt; noteEl.hidden = false; }

    var cutEl = q(root, '[data-mship-cutoff]');
    if (cutEl) {
      var note = cutoffNote(ch, bundle, iso(ship), t && t.days);
      cutEl.textContent = note.text;
      cutEl.hidden = false;
    }

    /* Cutoff + estimate note live inside a "Shipping details" tooltip rather than
       two standing paragraphs, to keep the box short. Hide the whole trigger when
       neither line has any text so an empty tooltip never opens. */
    var moreLine = q(root, '[data-mship-more-line]');
    if (moreLine) {
      var hasTip = !!((cutEl && cutEl.textContent.trim()) || (noteEl && noteEl.textContent.trim()));
      moreLine.hidden = !hasTip;
      if (hasTip) wireTip(root);
    }

    wireDestControls(root, function () { renderBlock(root); });
    root.hidden = false;
  }

  /* The "estimating for ZIP nnnnn / change" control. Shared by the PDP block
     and the cart block so the shopper only ever types a ZIP once. */
  function wireDestControls(root, redraw) {
    var edit = q(root, '[data-mship-dest-edit]');
    var form = q(root, '[data-mship-dest-form]');
    var input = q(root, '[data-mship-dest-zip]');
    var save = q(root, '[data-mship-dest-save]');
    var clear = q(root, '[data-mship-dest-clear]');
    if (!form || !input || !save) return;
    if (root.dataset.mshipWired === '1') return;
    root.dataset.mshipWired = '1';

    if (edit) {
      edit.addEventListener('click', function () {
        form.hidden = !form.hidden;
        if (!form.hidden) input.focus();
      });
    }
    function commit() {
      var z = (input.value || '').replace(/\D/g, '').slice(0, 5);
      if (z.length !== 5) { input.setAttribute('aria-invalid', 'true'); return; }
      input.removeAttribute('aria-invalid');
      saveDest(z, '');
      form.hidden = true;
      document.dispatchEvent(new CustomEvent('mariposa:dest-changed', { detail: { zip: z } }));
      redraw();
    }
    save.addEventListener('click', commit);
    input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); commit(); } });
    if (clear) {
      clear.addEventListener('click', function () {
        clearDest();
        input.value = '';
        document.dispatchEvent(new CustomEvent('mariposa:dest-changed', { detail: { zip: '' } }));
        redraw();
      });
    }
  }

  /* The "Shipping details" disclosure. Click/tap toggles the tooltip; there is no
     hover-only path, so it behaves the same on a phone as on a desktop. Wired once
     per block; closes on Escape and on a click outside it. */
  function wireTip(root) {
    if (root.dataset.mshipTipWired === '1') return;
    var btn = q(root, '[data-mship-more]');
    var tip = q(root, '[data-mship-tip]');
    if (!btn || !tip) return;
    root.dataset.mshipTipWired = '1';
    function close() { tip.hidden = true; btn.setAttribute('aria-expanded', 'false'); }
    function open() { tip.hidden = false; btn.setAttribute('aria-expanded', 'true'); }
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      if (tip.hidden) open(); else close();
    });
    document.addEventListener('click', function (e) {
      if (!tip.hidden && e.target !== btn && !btn.contains(e.target) && !tip.contains(e.target)) close();
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !tip.hidden) close(); });
  }

  /* ------------------------------------------------- merged cart paragraph

     Eileen, 2026-10-06: "Can we please merge the highlighted text and make
     editable in the Mariposa App. Then we can customize as needed."

     The paragraph she highlighted was assembled from five strings in three
     places. channels.<ch>.cart_note is one field holding the whole thing; the
     Liquid prints it verbatim and this fills the one piece that cannot be
     typed — the shopper's own ZIP — wherever she writes {zip}. With no ZIP
     known yet the token reads "your delivery address", so the sentence is
     never left dangling and no ZIP is ever invented.

     Runs on every page the snippet can appear on, the cart drawer included,
     because the drawer has no schedule config and so no scheduler pass. */
  function applyCartNote() {
    var nodes = document.querySelectorAll('[data-ms-cart-note]');
    if (!nodes.length) return;
    var where = readDest() || { zip: '', state: '', source: 'none' };
    var label = where.zip ? 'ZIP ' + where.zip : (where.state || 'your delivery address');
    for (var i = 0; i < nodes.length; i++) {
      var raw = nodes[i].getAttribute('data-ms-cart-note-raw');
      if (raw === null) {
        raw = nodes[i].textContent;
        nodes[i].setAttribute('data-ms-cart-note-raw', raw);
      }
      nodes[i].textContent = raw.replace(/\{zip\}/g, label);
    }
  }

  /* Dawn rebuilds the drawer's inner markup on every cart change, which puts a
     fresh, un-substituted copy of the paragraph on screen. Subscribing to
     cartUpdate is not enough on its own: on a product page the drawer markup is
     written after that event, so the first add-to-cart showed a literal "{zip}"
     (seen live 2026-10-06). The <cart-drawer> element itself persists, so watch
     its subtree and substitute whenever a new copy appears. */
  function watchCartNote() {
    if (typeof window.MutationObserver !== 'function') return;
    var drawer = document.querySelector('cart-drawer') || document.getElementById('CartDrawer');
    if (!drawer) return;
    new window.MutationObserver(function () {
      var node = drawer.querySelector('[data-ms-cart-note]');
      if (node && !node.hasAttribute('data-ms-cart-note-raw')) applyCartNote();
    }).observe(drawer, { childList: true, subtree: true });
  }

  function initAll() {
    var blocks = document.querySelectorAll('[data-mship]');
    for (var i = 0; i < blocks.length; i++) renderBlock(blocks[i]);
    applyCartNote();
    watchCartNote();
  }

  window.MariposaShip = {
    nowLA: nowLA,
    iso: iso,
    parseIso: parseIso,
    fmtLong: fmtLong,
    fmtShort: fmtShort,
    fmtArrival: fmtArrival,
    dest: dest,
    destLabel: destLabel,
    estimateNote: estimateNote,
    groupNote: groupNote,
    saveDest: saveDest,
    clearDest: clearDest,
    normalizeTransit: normalizeTransit,
    transitFor: transitFor,
    nationalDays: nationalDays,
    fastestDays: fastestDays,
    rangeWords: rangeWords,
    neutralShip: neutralShip,
    shipBase: shipBase,
    weekEnd: weekEnd,
    shipDayAllowed: shipDayAllowed,
    rollToShipDay: rollToShipDay,
    addDeliveryDays: addDeliveryDays,
    arrival: arrival,
    cutoffNote: cutoffNote,
    wireDestControls: wireDestControls,
    applyCartNote: applyCartNote,
    render: renderBlock,
    init: initAll,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('mariposa:dest-changed', function () {
    // Two blocks can be on one page (a PDP with a drawer open). Keep them level.
    var blocks = document.querySelectorAll('[data-mship]');
    for (var i = 0; i < blocks.length; i++) renderBlock(blocks[i]);
  });
})();
